// https://developers.google.com/analytics/devguides/collection/ga4/reference/events
import {
    logDebug,
    logInfo,
    logWarn,
    logError,
    logEvent,
} from '../../tracing.js';
import {
    map,
    reduce,
    isArray,
    uniq,
    intersection,
    find,
    some,
    isNil,
    isEmpty,
    includes,
    hasIn,
    result,
} from 'lodash';
import { validateGoogleData } from '../data/dataPageService.js';

import cookieService from '../cookieService.js';

import {
    extractAdwordsConfiguration,
    validateEcommerceData,
} from '../data/dataPageService.js';

const _prefix = 'ADS';

const _getDestinationId = (property) => {
    try {
        const adwordsConfiguration = extractAdwordsConfiguration();
        const countryCode = cookieService.getUserProfileOptions().countryCode;
        const defaultCountryCode = validateEcommerceData('defaultCountryCode');
        if (!adwordsConfiguration[property]) {
            logError(`${_prefix} ${property} is not defined`);
            throw new Error(` ${property} is not defined`);
        }

        const valueForSelectedCountry =
            adwordsConfiguration[property][countryCode];
        if (
            isNil(valueForSelectedCountry) ||
            isEmpty(valueForSelectedCountry)
        ) {
            logInfo(
                `${_prefix} ${property} for ${countryCode} is not defined...falling back to default.`
            );
            const valueForDefaultCountry =
                adwordsConfiguration[property][defaultCountryCode];

            if (
                isNil(valueForDefaultCountry) ||
                isEmpty(valueForDefaultCountry)
            ) {
                throw new Error(
                    `${property} for defaultCountryCode ${defaultCountryCode} is not defined`
                );
            }
            return valueForDefaultCountry;
        }
        return valueForSelectedCountry;
    } catch (error) {
        logError(`${_prefix} ${error.message}`);
        throw new Error(error.message);
    }
};

const _getConversionId = () => _getDestinationId('conversionId');
const _getConversionLabel = () => _getDestinationId('conversionLabel');
const _getInitiateCheckoutLabel = () =>
    _getDestinationId('initiateCheckoutLabel');
const _getAddToCartLabel = () => _getDestinationId('addToCartLabel');
const _getNewsletterLabel = () => _getDestinationId('newsletterLabel');
const _getValuePropertyForCountryWithFallback = ({
    property,
    fallbackValue,
}) => {
    const adwordsConfiguration = extractAdwordsConfiguration();
    const countryCode = cookieService.getUserProfileOptions().countryCode;
    const defaultCountryCode = validateEcommerceData('defaultCountryCode');
    if (!hasIn(adwordsConfiguration, property)) {
        logError(`${_prefix} ${property} is not defined`);
        return fallbackValue;
    }

    if (!hasIn(adwordsConfiguration, property + '.' + countryCode)) {
        logWarn(
            `${property} not found for countryCode ${countryCode}. Falling back to ${defaultCountryCode}`
        );
        const resultFromDefaultCountryCode =
            result(adwordsConfiguration, property + '.' + defaultCountryCode) ||
            fallbackValue;
        logDebug(
            `Using value ${resultFromDefaultCountryCode} for property ${property}`
        );
        return resultFromDefaultCountryCode;
    }
    const resultFromMatchedCountryCode =
        result(adwordsConfiguration, property + '.' + countryCode) ||
        fallbackValue;
    return resultFromMatchedCountryCode;
};

const _getCategoriesConversionLabel = (conversionLabel) => {
    let fallbackValue = conversionLabel;
    try {
        const property = 'categoriesConversionLabel';
        return _getValuePropertyForCountryWithFallback({
            property,
            fallbackValue,
        });
    } catch (error) {
        logDebug(
            `${_prefix} no retrieval of categories conversion label. Maybe there is none? I will return empty string.`
        );
        return fallbackValue;
    }
};
const _getTrackedCategories = () => {
    let fallbackValue = [];
    try {
        const property = 'trackedCategories';
        return _getValuePropertyForCountryWithFallback({
            property,
            fallbackValue,
        });
    } catch (error) {
        logDebug(
            `${_prefix} no retrieval of tracked categories. Maybe there are none? I will return empty tracked categories.`
        );
        return fallbackValue;
    }
};

const _validateGtag = () => {
    if (!window.gtag) {
        throw new Error('window.gtag is null or undefined');
    }
};

const _mapItemsForDynamicRemarketing = (items) =>
    map(items, (item) => ({
        id: item.googleId,
        google_business_vertical: 'retail',
    }));

const beginCheckout = ({ cartId, currency, totaleuro, items }) => {
    _validateGtag();
    const conversionId = _getConversionId();
    const initiateCheckoutLabel = _getInitiateCheckoutLabel();
    if (!isNil(initiateCheckoutLabel) && !isEmpty(initiateCheckoutLabel)) {
        const beginCheckoutObj = {
            transaction_id: cartId, // transaction_id è documentato solo per purchase...?
            send_to: `${conversionId}/${initiateCheckoutLabel}`,
            currency,
            value: totaleuro,
            items: map(items, (item) => ({
                item_id: item.googleId,
                item_name: item.slug,
            })),
        };
        logEvent(`${_prefix} begin_checkout`, beginCheckoutObj);
        gtag('event', 'begin_checkout', beginCheckoutObj);
    }
};

const purchaseDynamicRemarketing = ({ value, items }) => {
    _validateGtag();
    const conversionId = _getConversionId();
    const obj = {
        send_to: conversionId,
        value,
        items: _mapItemsForDynamicRemarketing(items),
    };
    logEvent(`${_prefix} purchase dynamic remarketing`, obj);
    gtag('event', 'purchase', obj);
};

const purchaseConversion = ({ orderId, currency, totalEuro, items }) => {
    try {
        _validateGtag();
        // O quella della country o quella di default
        const conversionId = _getConversionId();
        // O quella della country o quella di default
        const conversionLabel = _getConversionLabel();
        logDebug(
            `conversionId ${conversionId} and conversionLabel ${conversionLabel}`
        );
        // O quella della country o quella di default
        const categoriesConversionLabel =
            _getCategoriesConversionLabel(conversionLabel);
        // Quelle della country o quelle di default
        const trackedCategories = _getTrackedCategories();

        logDebug(
            `categoriesConversionLabel and trackedCategories`,
            categoriesConversionLabel,
            trackedCategories
        );

        const orderCategories = uniq(
            reduce(
                items,
                (acc, item) => {
                    const itms = map(
                        item.categories,
                        (category) => category.slug
                    );
                    acc = acc.concat(itms);
                    return acc;
                },
                []
            )
        );
        let send_to_address_label = conversionLabel || '';
        const foundMatchingCategories =
            intersection(orderCategories, trackedCategories).length > 0;
        if (foundMatchingCategories) {
            logDebug(
                `Tracked category found. TrackedCategories: ${trackedCategories}, Order categories: ${orderCategories}`
            );
            send_to_address_label = categoriesConversionLabel;
        }

        let conversionData = {
            transaction_id: orderId,
            send_to: `${conversionId}/${send_to_address_label}`,
            value: totalEuro,
            currency,
            items: map(items, (item) => {
                return {
                    id: item.googleId,
                    quantity: item.quantity,
                    price: item.price,
                };
            }),
        };
        logEvent('conversion', conversionData);
        if (window.enhanced_conversion_data) {
            console.log(
                'Enhanced conversion data',
                window.enhanced_conversion_data
            );

            logEvent('EnhancedConversionData', window.enhanced_conversion_data);
        }
        return window.gtag('event', 'conversion', conversionData);
    } catch (error) {
        logError(`purchaseConversion: ${error.message}`);
        throw new Error(`purchaseConversion: ${error.message}`);
    }
};

const newsletterRegistration = ({ url, email }) => {
    try {
        _validateGtag();
        const conversionId = _getConversionId();
        const newsletterLabel = _getNewsletterLabel();

        if (!isNil(newsletterLabel) && !isEmpty(newsletterLabel)) {
            let obj = {
                send_to: `${conversionId}/${newsletterLabel}`,
                url: url === undefined ? window.location.href : url,
                email,
                value: 1,
            };
            logEvent(`${_prefix} CompleteRegistration`, obj);
            gtag('event', 'CompleteRegistration', obj);
        }
    } catch (error) {
        logError(`${_prefix} ${error.message}`);
        throw new Error(error.message);
    }
};

const adsSearchItemResultsDynamicRemarketing = ({ searchterm, items }) => {
    _validateGtag();
    const viewItemObject = {
        search_term: searchterm,
        items: _mapItemsForDynamicRemarketing(items),
    };

    logEvent(
        `${_prefix} view_search_results dynamic remarketing`,
        viewItemObject
    );
    gtag('event', 'view_search_results', viewItemObject);
};

const adsViewItemListDynamicRemarketing = ({ items }) => {
    _validateGtag();
    const conversionId = _getConversionId();

    const obj = {
        send_to: conversionId,
        items: _mapItemsForDynamicRemarketing(items),
    };

    logEvent(`${_prefix} view_item_list dynamic remarketing`, obj);
    gtag('event', 'view_item_list', obj);
};

const adsViewItemDynamicRemarketing = ({ value, items }) => {
    _validateGtag();
    const conversionId = _getConversionId();
    const obj = {
        send_to: conversionId,
        value,
        items: _mapItemsForDynamicRemarketing(items),
    };

    logEvent(`${_prefix} view_item dynamic remarketing`, obj);
    gtag('event', 'view_item', obj);
};

const adsAddToCartDynamicRemarketing = ({ id, price }) => {
    _validateGtag();
    const conversionId = _getConversionId();
    const obj = {
        send_to: conversionId,
        value: price,
        items: [
            {
                id,
                google_business_vertical: 'retail',
            },
        ],
    };

    logEvent(`${_prefix} add_to_cart dynamic remarketing`, obj);
    gtag('event', 'add_to_cart', obj);
};

const adsAddToCartConversion = ({
    id,
    currency,
    price,
    items,
    transactionId,
}) => {
    _validateGtag();
    const conversionId = _getConversionId();
    const addToCartLabel = _getAddToCartLabel();
    if (!isNil(addToCartLabel) && !isEmpty(addToCartLabel)) {
        if (!some(items, (item) => item.googleId === id)) {
            logWarn(
                `Mismatch between serverside ids and current clientside id ${id} on add_to_cart. Serverside ids: ${map(
                    items,
                    (i) => i.googleId
                )}`
            );
        }

        let obj = {
            send_to: `${conversionId}/${addToCartLabel}`,
            currency,
            value: price,
        };
        if (!isNil(transactionId) && !isEmpty(transactionId)) {
            obj.transaction_id = transactionId;
        }

        logEvent(`${_prefix} add_to_cart conversion`, obj);
        gtag('event', 'add_to_cart', obj);
    }
};

const initConversions = () => {
    const conversionId = _getConversionId();
    const analytics4EnableDebug = validateGoogleData(
        'analytics4EnableDebug',
        false
    );

    const configAlreadySetConversionId = some(
        dataLayer,
        (d) => includes(d, 'config') && includes(d, conversionId)
    );
    if (!configAlreadySetConversionId) {
        if (analytics4EnableDebug === true) {
            gtag('config', conversionId, {
                debug_mode: analytics4EnableDebug, // Per ora uso lo stesso config
            });
        } else {
            gtag('config', conversionId);
        }
        logEvent(`Setting \'config\' with conversionId ${conversionId}`);
    }
};

export {
    beginCheckout,
    purchaseDynamicRemarketing,
    purchaseConversion,
    newsletterRegistration,
    adsSearchItemResultsDynamicRemarketing,
    adsViewItemListDynamicRemarketing,
    adsViewItemDynamicRemarketing,
    adsAddToCartDynamicRemarketing,
    adsAddToCartConversion,
    initConversions,
};
